import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import store from '@/store';
import { ErrorResponse } from '@/utils/response';
import { axios, CustomAxiosResponse } from '@/utils/axios';
import { Subject, SubjectDetail, SubjectType } from './subject.model';

export interface SubjectStoreInterface {
  subject: Subject | null;
  lowerFormSubjects: SubjectDetail[];
  upperFormSubjects: SubjectDetail[];
  elementarySubjects: SubjectDetail[];
  error: ErrorResponse | null;
  fetchSubject(params: FetchSubjectParams): Promise<void>;
  fetchLowerFormSubjects(): Promise<void>;
  fetchUpperFormSubjects(): Promise<void>;
  fetchElementaryFormSubjects(): Promise<void>;
}

export interface FetchSubjectParams {
  subjectUuid: string;
  subjectType: SubjectType | null;
}

@Module({
  namespaced: true,
  dynamic: true,
  name: 'subject',
  store,
})
class SubjectStore extends VuexModule {
  subject: Subject | null = null;
  lowerFormSubjects: SubjectDetail[] = [];
  upperFormSubjects: SubjectDetail[] = [];
  elementarySubjects: SubjectDetail[] = [];
  error: ErrorResponse | null = null;

  @Mutation
  updateSubject(params: Subject) {
    this.subject = params;
  }

  @Mutation
  updateLowerFormSubjects(params: SubjectDetail[]) {
    this.lowerFormSubjects = params;
  }

  @Mutation
  updateUpperFormSubjects(params: SubjectDetail[]) {
    this.upperFormSubjects = params;
  }

  @Mutation
  updateElementarySubjects(params: SubjectDetail[]) {
    this.elementarySubjects = params;
  }

  @Mutation
  setError(params: ErrorResponse) {
    this.error = params;
  }

  @Action
  async fetchSubject(params: FetchSubjectParams): Promise<void> {
    this.context.commit('updateSubject', null);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(
        `subjects/${params.subjectUuid}`,
        {
          params: {
            subject_type: params.subjectType,
          },
        },
      );

      this.context.commit('updateSubject', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async fetchLowerFormSubjects(): Promise<void> {
    this.context.commit('updateLowerFormSubjects', []);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(
        `subjects/lower_form`,
      );

      this.context.commit('updateLowerFormSubjects', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async fetchUpperFormSubjects(): Promise<void> {
    this.context.commit('updateUpperFormSubjects', []);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(
        `subjects/upper_form`,
      );

      this.context.commit('updateUpperFormSubjects', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async fetchElementaryFormSubjects(): Promise<void> {
    this.context.commit('updateElementarySubjects', []);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(
        `subjects/elementary`,
      );

      this.context.commit('updateElementarySubjects', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }
}

export default getModule(SubjectStore);
